import {HttpClient} from '@angular/common/http';
import {computed, Injectable, signal, WritableSignal} from '@angular/core';
import {environment} from 'src/environments/environment.development';
import {Param} from "../interfaces/param-interface";
import {Contacto, Documento, PersonaInfo} from "../interfaces/shared-interface";
import {ArchivoItem} from "../../users/interfaces/proyecto.interface";
import Swal from "sweetalert2";

const apiVucUrl = environment.apiVucUrl
@Injectable({providedIn: 'root'})
export class PersonasService {
    constructor(private http: HttpClient) {
    }
    private _persons: WritableSignal<PersonaInfo[]> = signal([])
    /**
     * La persona encontrada en la ventana de busqueda. Este campo debe ser reseteado por cada seleccion o cuando se necesita agregar más de una persona en el mismo contexto.
     */
    private _selected: WritableSignal<PersonaInfo> = signal(null!)
    private _noresults: WritableSignal<boolean> = signal(false)
    archivos: WritableSignal<ArchivoItem[]> = signal([])
    validTemplateResponse:WritableSignal<boolean> = signal(false)
    //Validar el comportamiento del botones cuando se seleccione el titular
    validStatuConsultaPorTitular:WritableSignal<boolean> = signal(false)
    /**
     * Reglas de validacion a aplicar a los documentos
     */
    private docRules = [
        {id: 1, desc: "DUI", maxLen: 10, minLen: 9},
        {id: 2, desc: "NIT", maxLen: 17, minLen: 14},
        {id: 3, desc: "PASAPORTE", maxLen: 25, minLen: 0},
        {id: 4, desc: "CARNET DE RESIDENTE", maxLen: 25, minLen: 0}
    ];
    archivosLoad:boolean=false;
    textDocNombre:string=""
    consultaInfo(params: Param[]) {
        return this.http.post<PersonaInfo[]>(apiVucUrl + `api/v1.0/personas/info`, params, {observe: 'response'})
    }
    consultaInfoPorTipoDocumento(documento: String | null, idTipoDocumento: number | undefined, uniqueId:string | null) {
        if ((documento === null || idTipoDocumento === null || idTipoDocumento === undefined) && (uniqueId === null || uniqueId === 'null' || uniqueId === "0")) {
            return;
        }
        let params: Param[] = [];
        if(documento !== null && idTipoDocumento !== null && idTipoDocumento !== undefined){ // CASO 1: POR TIPO DOCUMENTO
            params  = [{key: "documento", value: this.sanitizarDocumento(documento!)}, {
                key: "tipo_documento",
                value: idTipoDocumento.toString()
            }];
        }

        if(uniqueId !== null && uniqueId !== 'null'){// CASO 2: POR UNIQUEID
            params  = [{key: "uniqueids", value: uniqueId}];
        }

        this._selected.set(null!)
        return this.consultaInfo(params)
            .subscribe({
                next: (result) => {
                    this._persons.set(this.sanitizarDatos(result.body!))
                    this._selected.set(this._persons()[0])
                    this._noresults.set(false)
                    this.validTemplateResponse.set(true)
                },
                error: (error) => {
                    this._noresults.set(true)
                    console.log(error)
                    if(this.noresults()){
                        Swal.fire({
                            icon: "error",
                            title: "Error",
                            text: "No se encontraron datos",
                            showDenyButton: true,
                            showConfirmButton: false,
                            denyButtonText: `Ok`
                        }).then(r => console.log(`No se encontraros datos`));
                    }
                    this.validTemplateResponse.set(false);
                }, complete: () => {
                }
            });
    }
    findContactoByTipo(contactos: Contacto[], tipo: string): Contacto | undefined {
        return contactos.find(contacto => contacto.tipo === tipo);
    }
    /**Metodo que  encuentra la persona por documento*/
    findDocumentoByTipo(documentos: Documento[], tipo: string): Documento | undefined {
        return documentos.find(documento => documento.tipo === tipo);
    }
    /**Metodo que  encuentra la persona*/
    findDocumentoFirst(documentos: Documento[]): Documento | undefined {
        if (documentos == null) {
            return;
        }
        return documentos.find(documento => documento.tipo === documento.tipo);
    }
    /**
     * remueve objetos invalidos y registros duplicados
     */
    private sanitizarDatos(personas: PersonaInfo[]): PersonaInfo[] {
        if (!Array.isArray(personas) || personas.length === 0) {
            return [];
        }
        return personas.map(persona => {
            const validContactos = persona.contactos.filter(contacto =>
                contacto.idContacto !== null &&
                contacto.tipo !== null &&
                contacto.contacto !== null
            );
            const uniqueContactos = removeDuplicates(validContactos, contacto =>
                `${contacto.idContacto}|${contacto.tipo}|${contacto.contacto}`
            );
            const validDocumentos = persona.documentos.filter(documento =>
                documento.idDocumento !== null &&
                documento.tipo !== null &&
                documento.documento !== null
            );
            const uniqueDocumentos = removeDuplicates(validDocumentos, documento =>
                `${documento.idDocumento}|${documento.tipo}|${documento.documento}`
            );
            return {
                ...persona,
                contactos: uniqueContactos,
                documentos: uniqueDocumentos
            };
        });
        function removeDuplicates<T>(items: T[], keyExtractor: (item: T) => string): T[] {
            const seen = new Set<string>();
            return items.filter(item => {
                const key = keyExtractor(item);
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            });
        }
    }
    /**
     * Retorna solo los numeros contenidos en el input.
     */
    sanitizarDocumento(input: String): string {
        if (input === null) {
            return '';
        }
        return input.replace(/[^a-zA-Z0-9]/g, '');
    }
    get selected(): WritableSignal<PersonaInfo> {
        return this._selected;
    }
    get noresults(): WritableSignal<boolean> {
        return this._noresults;
    }
    /**
     * limpia la persona seleccionada en pantalla
     */
    limpiarSeleccion() {
        this._selected.set(null!);
        this._noresults.set(false)
    }
    /**
     * Aplica reglas de validacion a los tipos de documento especificados.
     */
    validarDocumentos(providedDocuments: { [key: number]: string }): boolean {
        let docRulesFiltered = this.docRules.filter(doc => providedDocuments.hasOwnProperty(doc.id));
        for (const doc of docRulesFiltered) {
            let providedValue = providedDocuments[doc.id];
            if (!providedValue || providedValue.trim() === '') {
                console.error(`"${doc.desc}" null o vacio.`);
                return false;
            }
            if (providedValue.length < doc.minLen) {
                console.error(`"${doc.desc}" no cumple con longitud minima requerida.`);
                return false;
            }
            if (providedValue.length > doc.maxLen) {
                console.error(`"${doc.desc}" excede la longitud maxima permitida.`);
                return false;
            }
        }
        return true;
    }
    private zeroFill({doc, providedValue}: {
        doc: { minLen: number; id: number; desc: string; maxLen: number },
        providedValue: string
    }) {
        if (doc.id === 1 && providedValue && /^\d+$/.test(providedValue) && providedValue.length < doc.minLen) {
            providedValue = providedValue.padStart(doc.minLen, '0');
        }
        return providedValue;
    }
    getObtenerArchivosPersona(tipos:string[],uniqueId: string) {
        let maxResult=10;
        let url = apiVucUrl + `api/v1.0/personas/${uniqueId}/archivos?tipos=${tipos}&maxResult=${maxResult}`;
        return this.http.get<ArchivoItem[]>(url, {observe: 'response'}).subscribe({
            next: (result) => {
                this.archivos.set(result.body!);
                this.archivosLoad=true
            },
            error: (error) => {
                console.log(error)
                this.archivos.set([]);
            },
            complete(){
            }
        });
    }

    /**Obtiene nombre de tipo de documento para el label cuando se realice la busqueda
     * Obtiene el primer caracter del texto para que sea mayuscula
     * Luego convierte lo siguientes caracteres a minusculas*/
    labelDoc(typeDocSeleted: string): string {
        if (!typeDocSeleted) return ''; // Si está vacío, se devuelve una cadena vacia
        return typeDocSeleted.charAt(0).toUpperCase() + typeDocSeleted.slice(1).toLowerCase();
    }

    /**
     * Obtener la informacion de las personas por uniqueIds
     * */
    consultarInfoPorUniqueIds(uniqueIds:string){
        this.consultaInfoPorTipoDocumento(null,undefined,uniqueIds);
    }
    /**
     * Metodo que obtiene el tipo de documento texto que se visualiza dinamicamente en la respuesta */
    textTipoDoc = computed(() => {
        if (this.selected() && this.selected()?.uniqueId != null) {
            return this.labelDoc(this.findDocumentoFirst(this.selected().documentos)?.tipo!);
        }
        return "N/A";
    });
}
